import React, { useState, useEffect } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';

interface ImageViewerProps {
  images: string[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
  alt: string;
}

const ImageViewer = ({ images, initialIndex, isOpen, onClose, alt }: ImageViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
          }
          break;
        case 'ArrowRight':
          if (currentIndex < images.length - 1) {
            setCurrentIndex(currentIndex + 1);
          }
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length, onClose]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Handle tap to close on background
  const handleBackgroundClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Handle touch events for swipe-to-close
  const handleTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (!touchStart) return;
    
    const touch = event.changedTouches[0];
    const deltaY = touch.clientY - touchStart.y;
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    
    // If swipe down gesture is detected (vertical movement > 100px and mostly vertical)
    if (deltaY > 100 && deltaX < 50) {
      onClose();
    }
    
    setTouchStart(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-none w-screen h-screen p-0 bg-black/95 border-0"
        onClick={handleBackgroundClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header with close button and navigation info */}
        <div className="absolute top-4 left-4 right-4 z-50 flex justify-between items-center">
          <div className="text-white/70 text-sm">
            {images.length > 1 && `${currentIndex + 1} / ${images.length}`}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/20 h-12 w-12 md:h-10 md:w-10"
          >
            <X className="h-7 w-7 md:h-6 md:w-6" />
          </Button>
        </div>

        {/* Mobile close button at bottom for easier access */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 md:hidden">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-white hover:bg-white/20 bg-black/30 px-6 py-3 rounded-full"
          >
            Done
          </Button>
        </div>

        {/* Image navigation arrows */}
        {images.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 h-12 w-12 disabled:opacity-30"
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              disabled={currentIndex === images.length - 1}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 h-12 w-12 disabled:opacity-30"
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </>
        )}

        {/* Zoom controls */}
        <TransformWrapper
          initialScale={1}
          minScale={0.1}
          maxScale={10}
          centerOnInit
          wheel={{ step: 0.1 }}
          doubleClick={{ mode: 'toggle' }}
          pinch={{ step: 5 }}
          limitToBounds={false}
          centerZoomedOut={true}
          alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
          velocityAnimation={{ sensitivity: 1, animationTime: 0 }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => zoomOut()}
                  className="text-white hover:bg-white/20 h-10 w-10"
                >
                  <ZoomOut className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resetTransform()}
                  className="text-white hover:bg-white/20 px-3"
                >
                  Reset
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => zoomIn()}
                  className="text-white hover:bg-white/20 h-10 w-10"
                >
                  <ZoomIn className="h-5 w-5" />
                </Button>
              </div>
              
              <TransformComponent
                wrapperClass="!w-screen !h-screen !absolute !inset-0"
                contentClass="!w-full !h-full !flex !items-center !justify-center"
              >
                <img
                  src={images[currentIndex]}
                  alt={`${alt} - image ${currentIndex + 1}`}
                  className="select-none block"
                  style={{ 
                    maxWidth: 'none', 
                    maxHeight: 'none',
                    width: 'auto', 
                    height: 'auto',
                    objectFit: 'none'
                  }}
                  draggable={false}
                />
              </TransformComponent>
            </>
          )}
        </TransformWrapper>

        {/* Touch gesture hints */}
        <div className="absolute bottom-4 right-4 z-50 text-white/50 text-xs">
          <div className="hidden md:block">
            <div>Double tap to zoom</div>
            <div>Scroll wheel to zoom</div>
            {images.length > 1 && <div>← → to navigate</div>}
            <div>Click background to close</div>
          </div>
          <div className="md:hidden text-center">
            <div>Pinch to zoom</div>
            <div>Swipe down to close</div>
            <div>Tap background to close</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageViewer;